import type { ReactNode } from 'react';
import { Spinner } from '../atoms';

/** Currency formatter shared across portal pages. */
export function money(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
}

/** base64-encode a File's bytes (strips the data: URL prefix). Shared by the
 *  portal upload surfaces (Documents "Send us a document" + the request accordion's
 *  per-item fulfil upload). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Loading / error / empty wrapper for a portal query. */
export function QueryState({
  isLoading, error, isEmpty, emptyLabel = 'Nothing to show yet.', children,
}: {
  isLoading: boolean; error: unknown; isEmpty: boolean; emptyLabel?: string; children: ReactNode;
}) {
  if (isLoading) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;
  if (error) return <div style={{ padding: 24, color: 'var(--color-status-red, #b00)' }}>Couldn’t load this. Please try again.</div>;
  if (isEmpty) return <div style={{ padding: 24, color: 'var(--color-muted, #666)' }}>{emptyLabel}</div>;
  return <>{children}</>;
}

export interface PortalColumn<T> {
  header: string;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right';
}

/** A minimal, dependency-free table for portal lists (scrolls horizontally).
 *  Pass `onRowClick` to make rows navigable (cursor + keyboard-activatable) —
 *  used by the portal's read-only detail views (#37). */
export function PortalTable<T>({ columns, rows, getKey, onRowClick }: {
  columns: PortalColumn<T>[]; rows: T[]; getKey: (row: T, i: number) => string;
  onRowClick?: (row: T) => void;
}) {
  const clickable = !!onRowClick;
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--color-border, #e2e2df)', borderRadius: 8, background: 'var(--color-surface-raised, #fff)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th scope="col" key={i} style={{
                textAlign: c.align || 'left', padding: '10px 12px', whiteSpace: 'nowrap',
                borderBottom: '1px solid var(--color-border, #e2e2df)', color: 'var(--color-muted, #666)',
                fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.3,
              }}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={getKey(row, i)}
              onClick={clickable ? () => onRowClick!(row) : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onRowClick!(row); } : undefined}
              tabIndex={clickable ? 0 : undefined}
              style={clickable ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((c, j) => (
                <td key={j} style={{
                  textAlign: c.align || 'left', padding: '10px 12px',
                  borderBottom: '1px solid var(--color-border-subtle, #f0f0ee)',
                }}>{c.cell(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 style={{ fontSize: 20, margin: '0 0 16px', fontWeight: 600 }}>{children}</h2>;
}
